import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, map, of, Subject} from "rxjs";
import {PublicationPost} from "../models/publication.model";
import {CTMSMilestonePost} from "../models/ctmsmilestone.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import {FeedbackService} from "./feedback.service";
import {CreateObject, DataObject, PostBase, PostObjectResponse} from "../models/metadata";
import {CustomMilestone, CustomMilestonePost} from "../models/custom-milestone.model";
import {
  AddExternalProfileActions,
  AddExternalProfileResponse,
  ExternalProfile,
  ExternalProfileEndpointResponse,
} from "../models/external-profile.model";
import {Activity, ActivityPost} from "../models/activity.model";

const ROUTES = {
  'publication': '/api/publications/edit',
  'ctmsmilestone': '/api/ctmsmilestones/edit',
  'custom-milestones-edit': '/api/custom-milestones/edit',
  'custom-milestones-create': '/api/custom-milestones/create',
  'custom-milestones-delete': '/api/custom-milestones/delete',
  'activity-edit': '/api/custom-activities/edit',
  'activity-create': '/api/custom-activities/create',
  'activity-delete': '/api/custom-activities/delete',
  'external-profiles-some': '/api/custom-activities/external/some',
  'external-profiles-add': '/api/custom-activities/external/add'
}

const DEFAULT_DELETE_RESPONSE = false

const DEFAULT_POST_RESPONSE: PostObjectResponse<never> = {
  success: false,
  updatedData: undefined
}

const DEFAULT_EXTERNAL_PROFILE_RESPONSE = {
  externalProfiles: []
} as ExternalProfileEndpointResponse

const DEFAULT_ADD_EXTERNAL_PROFILE_RESPONSE: AddExternalProfileResponse = {
  actionPerformed: AddExternalProfileActions.INTERNAL_ERROR,
  addedPerson: new ExternalProfile()
}

@Injectable({
  providedIn: 'root'
})
export class DetailedViewObjectService {
  private objectSubject = new BehaviorSubject<CreateObject | undefined>(undefined)
  detailedObject = this.objectSubject.asObservable()
  constructor(private http: HttpClient, private feedbackService: FeedbackService) {
  }

  public nextDetailedObject(o: CreateObject) {
    console.log('DetailedViewObjectService.nextDetailedObject called with:', o);
    this.objectSubject.next(o)
  }

  public deleteObject(o: DataObject){
    const type = this.getDeleteType(o)

    if (!type) {
      console.error('Object type not supported for POST request.', o);
      this.feedbackService.emitError("Unexpected error. Unable to save changes")
      return of(DEFAULT_DELETE_RESPONSE);
    }

    const responseWrapperSubject: Subject<boolean> = new Subject()
    const observable = this.http.post<boolean>(ROUTES[type], o)
      .pipe(catchError(this.feedbackService.handleError('Delete entry', DEFAULT_DELETE_RESPONSE)),)

    observable.subscribe(res => {
      if(res)
        this.feedbackService.emitSuccess("Changes were successfully saved")
      else
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.")
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  public postObject<T extends PostBase, R extends DataObject>(o: T){
    const type = this.getPostType(o)
    if (!type) {
      console.error('Object type not supported for POST request.', o);
      this.feedbackService.emitError("Unexpected error. Unable to save changes")
      return of(DEFAULT_POST_RESPONSE)
    }
    const responseWrapperSubject: Subject<PostObjectResponse<R>> = new Subject()
    const observable = this.http.post<PostObjectResponse<R>>(ROUTES[type], o)
      .pipe(
        catchError(this.feedbackService.handleError('save changes', DEFAULT_POST_RESPONSE)),
        map(res => o.getSourceWrapper()(res))
      )

    observable.subscribe(res => {
      if(res.success)
        this.feedbackService.emitSuccess("Changes were successfully saved")
      else
        this.feedbackService.emitError("Unable to save changes. Please try again after refreshing the page.")
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  getExternalProfilesWithID(...eIDs: Array<string>){
    const responseWrapperSubject = new Subject<ExternalProfileEndpointResponse>()
    const profileParams = new HttpParams({
      fromObject: { 'eIDs[]': eIDs }
    })
    const observable = this.http.get<ExternalProfileEndpointResponse>(ROUTES['external-profiles-some'], {params: profileParams})
      .pipe(
        catchError(this.feedbackService.handleError('getExternalProfiles', DEFAULT_EXTERNAL_PROFILE_RESPONSE)),
        map(res => this.parseExternalProfileResponse(res))
      )

    observable.subscribe(res => {
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  postVeevaLinkProfileURL(veevaURL: string){
    const responseWrapperSubject: Subject<AddExternalProfileResponse> = new Subject()
    const postBody = {
      veevaURL
    }
    const observable = this.http.post<AddExternalProfileResponse>(ROUTES["external-profiles-add"], postBody)
      .pipe(
        catchError(this.feedbackService.handleError('add profile', DEFAULT_ADD_EXTERNAL_PROFILE_RESPONSE)),
        map(res => this.parseAddExternalProfileResponse(res))
      )

    observable.subscribe(res => {
      if(res.actionPerformed === AddExternalProfileActions.ADD)
        this.feedbackService.emitSuccess("Profile was successfully added")
      else if(res.actionPerformed === AddExternalProfileActions.UPDATE)
        this.feedbackService.emitInfo("Profile already existed and was updated")
      else if(res.actionPerformed === AddExternalProfileActions.ERROR)
        this.feedbackService.emitError("Profile could not be found")
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  private getPostType(o: PostBase): keyof typeof ROUTES | undefined {
    return o instanceof PublicationPost ? "publication"
      : o instanceof CTMSMilestonePost ? "ctmsmilestone"
        : o instanceof CustomMilestonePost ? (o.ccmID? 'custom-milestones-edit' : 'custom-milestones-create')
          : o instanceof ActivityPost ? (o.acID? 'activity-edit' : 'activity-create')
            : undefined
  }

  private getDeleteType(o: DataObject): keyof typeof ROUTES | undefined{
    return o instanceof CustomMilestone? 'custom-milestones-delete'
           : o instanceof Activity? 'activity-delete' : undefined
  }

  private parseExternalProfileResponse(res: ExternalProfileEndpointResponse): ExternalProfileEndpointResponse{
    const externalProfiles = res.externalProfiles.map(ep => Object.assign(new ExternalProfile(), ep))
    return {externalProfiles}
  }

  private parseAddExternalProfileResponse(res: AddExternalProfileResponse): AddExternalProfileResponse{
    res.addedPerson = Object.assign(new ExternalProfile(), res.addedPerson)
    return res
  }
}
